import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  buildDuplicateAdminProspectLetterTemplateRequest,
  createAdminProspectLetterTemplateWriteAttempt,
  duplicateAdminProspectLetterTemplate,
  ADMIN_PROSPECT_LETTER_TEMPLATE_NAME_MAX,
} from '../../services/admin/prospectLetterTemplatesService.js';
import {
  adminProspectLetterTemplateErrorKeyFromError,
  classifyAdminCallableError,
} from './adminProspectLetterTemplateDisplay.js';

export default function AdminDuplicateLetterTemplateDialog({
  open,
  sourceTemplate,
  sourceVersion,
  onClose,
  onSuccess,
}) {
  const titleId = useId();
  const descId = useId();
  const { t } = useLanguage();
  const { impersonation } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [retryable, setRetryable] = useState(false);
  const duplicateAttemptRef = useRef(createAdminProspectLetterTemplateWriteAttempt());
  const submitLockRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    duplicateAttemptRef.current.reset();
    setName(
      sourceTemplate?.name
        ? t('admin.prospection.letterTemplate.duplicate.defaultName').replace(
            '{name}',
            sourceTemplate.name,
          )
        : '',
    );
    setSaving(false);
    setErrorKey(null);
    setRetryable(false);
  }, [open, sourceTemplate?.templateId, sourceTemplate?.name]);

  if (!open || !sourceTemplate?.templateId) return null;

  const impersonating = impersonation?.active === true;
  const canSubmit =
    !impersonating &&
    !saving &&
    name.trim().length > 0 &&
    name.trim().length <= ADMIN_PROSPECT_LETTER_TEMPLATE_NAME_MAX;

  const handleSubmit = async () => {
    if (!canSubmit || submitLockRef.current || impersonating) return;
    submitLockRef.current = true;
    setSaving(true);
    setErrorKey(null);
    setRetryable(false);
    try {
      const versionFingerprint =
        sourceVersion != null ? String(sourceVersion) : 'current';
      const payload = duplicateAttemptRef.current.resolveAttemptPayload({
        fingerprint: JSON.stringify({
          templateId: sourceTemplate.templateId,
          version: versionFingerprint,
          name: name.trim(),
        }),
        build: (operationId) =>
          buildDuplicateAdminProspectLetterTemplateRequest({
            operationId,
            templateId: sourceTemplate.templateId,
            name: name.trim(),
            version: sourceVersion ?? undefined,
          }),
      });
      const result = await duplicateAdminProspectLetterTemplate(payload);
      duplicateAttemptRef.current.discard();
      const templateId = result?.templateId;
      if (!templateId) throw new Error('missing-template-id');
      if (typeof onSuccess === 'function') onSuccess(templateId);
      else navigate(`/admin/prospection/letter-templates/${templateId}`);
    } catch (err) {
      setErrorKey(adminProspectLetterTemplateErrorKeyFromError(err));
      setRetryable(classifyAdminCallableError(err) === 'unavailable');
    } finally {
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl">
        <h2 id={titleId} className="text-lg font-semibold text-slate-900">
          {t('admin.prospection.letterTemplate.duplicate.title')}
        </h2>
        <p id={descId} className="mt-1 text-sm text-slate-600">
          {t('admin.prospection.letterTemplate.duplicate.subtitle')}
        </p>
        {impersonating ? (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
            {t('admin.prospection.letterTemplate.impersonationBlocked')}
          </p>
        ) : null}
        <label className="mt-4 block text-sm font-medium text-slate-700" htmlFor="duplicate-template-name">
          {t('admin.prospection.letterTemplate.field.name')}
        </label>
        <input
          id="duplicate-template-name"
          type="text"
          value={name}
          maxLength={ADMIN_PROSPECT_LETTER_TEMPLATE_NAME_MAX}
          onChange={(event) => {
            duplicateAttemptRef.current.reset();
            setName(event.target.value);
          }}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        {errorKey ? (
          <p className="mt-3 text-sm text-rose-800" role="alert">
            {t(errorKey)}
            {retryable ? (
              <button type="button" className="ml-2 underline" onClick={handleSubmit}>
                {t('admin.prospection.letterTemplate.action.retry')}
              </button>
            ) : null}
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700"
          >
            {t('admin.prospection.letterTemplate.duplicate.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving
              ? t('admin.prospection.letterTemplate.duplicate.saving')
              : t('admin.prospection.letterTemplate.duplicate.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
